import { useState } from "react";
import { namingRights } from "../../../../engine/bench";
import type { PlayerId, TournamentState } from "../../../../engine/types";
import { Face } from "../../components/Face";
import type { Tournament } from "../../lib/useTournament";
import { Burst } from "./Burst";
import { NAME_TAKEN, nameTaken } from "./Join";
import { lengthOf, useSent } from "./sent";

const COUNT = ["no", "one", "two", "three", "four"];

/**
 * After a thrashing: the winner picks one of the losers and types what the wall calls them. Once
 * that is done all that is left is a one-line strip saying who they named and until when.
 */
export function NamingRight({ t, state, me }: { t: Tournament; state: TournamentState; me: PlayerId }) {
  const [target, setTarget] = useState<PlayerId | null>(null);
  const [name, setName] = useState("");
  const sent = useSent(t);
  const rights = namingRights(state, me);
  const losers = [...new Set(rights.flatMap((r) => r.losers))].filter((id) => state.players[id]);
  const named = Object.values(state.players).filter((p) => p.dub?.by === me);
  const max = state.config.bench.nicknameMax;
  const rounds = state.config.bench.dubRounds;
  const picked = target && losers.includes(target) ? target : null;
  const ready = picked !== null && lengthOf(name) > 0 && !sent.waiting;
  // the same rule the engine keeps: nobody else may already go by it (the one being renamed may keep their own)
  const taken = picked !== null && nameTaken(state, name, picked);

  const done = named.map((p) => (
    <p key={p.id} className="naming-done">
      You named {p.name} {p.dub!.nickname} until round {p.dub!.untilRound}
    </p>
  ));
  if (!losers.length) return done.length ? <>{done}</> : null;
  return (
    <section className="sticker naming">
      {losers.length > 0 && (
        <>
          <div className="naming-top">
            <h2>Naming right</h2>
            <Burst>Won</Burst>
          </div>
          <p>
            You won by {state.config.bench.dubMargin} or more, so you may rename one of them. The wall uses it for{" "}
            {COUNT[rounds] ?? rounds} rounds.
          </p>
          <div className="naming-losers">
            {losers.map((id) => {
              const p = state.players[id]!;
              return (
                <button
                  key={id}
                  type="button"
                  className={"bench-chip" + (picked === id ? " on" : "")}
                  aria-pressed={picked === id}
                  onClick={() => setTarget(id)}
                >
                  <Face name={p.name} src={t.faceOf(p)} />
                  <span>{p.name}</span>
                </button>
              );
            })}
          </div>
          {picked && (
            <form
              className="bench-type"
              onSubmit={(e) => {
                e.preventDefault();
                if (!ready || taken) return;
                sent.send({ type: "player.dubbed", player: me, target: picked, nickname: name });
                setName("");
              }}
            >
              <input
                type="text"
                value={name}
                placeholder="Their new name"
                aria-label={`A new name for ${state.players[picked]!.name}`}
                autoComplete="off"
                autoCapitalize="words"
                spellCheck={false}
                enterKeyHint="send"
                onChange={(e) => setName([...e.target.value].slice(0, max).join(""))}
              />
              <button type="submit" className="btn accent" disabled={!ready || taken}>
                Name them
              </button>
              <span className="join-count">
                {[...name].length}/{max}
              </span>
            </form>
          )}
          {picked && taken && (
            <p className="player-refused" role="alert">
              {NAME_TAKEN}
            </p>
          )}
          {sent.refused && (
            <p className="player-refused" role="alert">
              Not sent: {sent.refused}
            </p>
          )}
        </>
      )}
      {done}
    </section>
  );
}
